import type { AccessMode } from '../landing/landing.interface.js'

/**
 * The beacon kinds `POST /tr/events` accepts (TO-165). Each value equals the
 * domain event the ingest worker publishes for it.
 */
export enum TrackingEventKind {
	OFFER_OPENED = 'offer.opened',
	OFFER_CTA_CLICKED = 'offer.cta_clicked',
}

/**
 * Claims carried by a landing-session tracking token. Minted by the landing BFF
 * per served page, verified by tracking-service; the only source of tenant,
 * offer and render for a beacon.
 */
export interface TrackingTokenClaims {
	tenantId: string
	offerId: string
	renderId: string
	/** Expiry, epoch seconds. */
	exp: number
	/** How the served view was admitted (TO-164); absent on pre-TO-164 tokens. */
	accessMode?: AccessMode
	/** The redeemed OTP / MAGIC_LINK challenge id (TO-164); OTP / MAGIC_LINK sessions only. */
	verifiedChallengeId?: string
}

/**
 * The message `POST /tr/events` enqueues on the tracking ingest queue. `ipHash`
 * is the HMAC of the caller IP — the raw IP never leaves the request handler.
 */
export interface TrackingIngestMessage {
	kind: TrackingEventKind
	tenantId: string
	offerId: string
	renderId: string
	sessionId: string
	ipHash: string
	/** Server receipt time, ISO 8601 — every window and event timestamp uses it. */
	receivedAt: string
	/** Copied from the tracking token (TO-164); absent when the token has none. */
	accessMode?: AccessMode
	/** Copied from the tracking token (TO-164); absent when the token has none. */
	verifiedChallengeId?: string
}

// ─── Email engagement tracking (TO-136) ─────────────────────────────────────

/**
 * The email hits tracking-service enqueues from `GET /tr/o/{token}` (open pixel)
 * and `GET /tr/c/{token}` (link redirect). Deliberately NOT members of
 * {@link TrackingEventKind}, so the `POST /tr/events` beacon can never accept them.
 */
export enum EmailTrackingKind {
	EMAIL_OPENED = 'email.opened',
	EMAIL_CLICKED = 'email.clicked',
}

/** Which tracked email element a token was minted for. */
export enum EmailTrackingTokenKind {
	OPEN = 'eo',
	CLICK = 'ec',
}

/** Stable error codes of the public email tracking routes. */
export enum TrackingErrorCode {
	/** `GET /tr/c/{token}`: malformed, unverifiable, wrong-kind or non-http(s)-target token. */
	TRACKING_LINK_INVALID = 'TRACKING_LINK_INVALID',
}

/** Claims of an email open-pixel token; the only source of tenant, notification and customer for an open. */
export interface EmailOpenTokenClaims {
	tenantId: string
	notificationId: string
	customerId: string
	/** Expiry, epoch seconds (send time + 365 days). */
	exp: number
}

/** Claims of an email link token: the open claims plus the link position and its signed target. */
export interface EmailClickTokenClaims extends EmailOpenTokenClaims {
	/** 0-based position of the link in the email HTML. */
	linkIndex: number
	/** The original `http`/`https` target the redirect sends to. */
	url: string
}

/** Enqueued by `GET /tr/o/{token}`. `ipHash` is the HMAC of the caller IP. */
export interface EmailOpenedIngestMessage {
	kind: EmailTrackingKind.EMAIL_OPENED
	tenantId: string
	notificationId: string
	customerId: string
	ipHash: string
	/** Server receipt time, ISO 8601. */
	receivedAt: string
}

/** Enqueued by `GET /tr/c/{token}` for an unexpired token. */
export interface EmailClickedIngestMessage {
	kind: EmailTrackingKind.EMAIL_CLICKED
	tenantId: string
	notificationId: string
	customerId: string
	linkIndex: number
	ipHash: string
	/** Server receipt time, ISO 8601. */
	receivedAt: string
}

export type EmailTrackingIngestMessage = EmailOpenedIngestMessage | EmailClickedIngestMessage

/** Any message on the tracking ingest queue: a landing beacon or an email hit (TO-136). */
export type AnyTrackingIngestMessage = TrackingIngestMessage | EmailTrackingIngestMessage
